import {SolicitacaoTipoPesquisa} from "@components/solicitacoes/SolicitacaoTipoPesquisa.tsx";
import {useAlert} from "@contexts/hooks/useAlert.ts";
import type {IContrato} from "@models/contratos/contrato.js";
import {ISolicitacao, ISolicitacaoTipo} from "@models/solicitacoes/solicitacoes.js";
import {Badge, Button, Grid, IconButton, Paper, TextField, Typography} from "@mui/material";
import {AddOutlined, DeleteOutlined, LinkOutlined, Upload} from '@mui/icons-material';
import {ToMoney} from '@shared/MathHelp.ts';
import React, {memo, useCallback, useEffect, useMemo, useState} from "react";
import {IContaContabil} from "@models/contaContabil/contaContabil.ts";
import {IRecurso} from "@models/recurso/IRecurso.ts";
import {DefaultPageProps} from "@models/DefaultProps.ts";
import {DefaultContainer} from "@shared/DefaultContainer.tsx";
import {RecursoPesquisa} from "@components/recurso/RecursoPesquisa.tsx";
import {ContaContabilPesquisa} from "@components/contaContabil/ContaContabilPesquisa.tsx";
import {ProdutoPesquisa} from "@components/produtos/ProdutoPesquisa.tsx";
import {ContratoPesquisa} from "@components/contratos/ContratoPesquisa.tsx";
import {IntInputEntry, MoneyInputEntry} from "@shared/specialComponents/InputEntry.tsx";
import {ISolicitacaoParametro} from "@models/solicitacoes/solicitacaoParametro.ts";
import {
    ICreateSolicitacaoDto,
    ICreateSolicitacaoItemDto,
    ICreateSolicitacaoItemRefDto
} from "@components/solicitacoes/dto/input/ICreateSolicitacaoDto.ts";
import {useSolicitacao} from "@components/solicitacoes/hooks/useSolicitacao.tsx";
import {useValidacao} from "@shared/hooks/useValidacao.ts";
import {fullHeight, fullWidth} from "@models/helpers.ts";
import lodash from 'lodash'
import {PermissionableIcon} from "@shared/specialComponents/PermissionableIcon.tsx";

type IParamRule = boolean | 'obrigatorio';

interface CreateSolicitacaoPageProps extends DefaultPageProps {
    recurso?: IRecurso,
    onCreate?: (s: ISolicitacao) => void
}

const RefMemo = memo((props: {
    value: ICreateSolicitacaoItemRefDto,
    setValue: (v: ICreateSolicitacaoItemRefDto) => void,
    onDelete?: (v: ICreateSolicitacaoItemRefDto) => void,
}) => {
    const {value, setValue} = props
    const {isUrlValido} = useValidacao()

    const urlValid = useMemo(() => {
        return isUrlValido(value.url);
    }, [value.url]);

    // const hasDiff = useMemo(() => {
    //     return !lodash.isEqual(value, props.value);
    // }, [value, props.value])
    //
    // Só atualiza o componente principal caso o url esteja num valor valido(previne re-render no componente principal)
    useEffect(() => {
        if (urlValid) props.setValue(value)
    }, [urlValid, value]);

    return <Grid container>
        <Grid>
            <TextField value={value.descricao} label={'Descrição'}
                       onChange={(e) => {
                           setValue({...value, descricao: e.target.value})
                       }}/>
        </Grid>
        <Grid>
            <TextField value={value.url} error={!urlValid} label={'Link'} onChange={(e) => {
                setValue({...value, url: e.target.value})
            }}/>
        </Grid>
        <Grid>
            <IconButton sx={{...fullHeight}} onClick={() => props.onDelete != null && props.onDelete(value)}>
                <DeleteOutlined/>
            </IconButton>
        </Grid>
    </Grid>
})

const RefsPageMemo = memo((props: {
    values: ICreateSolicitacaoItemRefDto[],
    setValues: (v: ICreateSolicitacaoItemRefDto[]) => void,
}) => {
    const {isUrlValido} = useValidacao()
    const [values, setValues] = useState<ICreateSolicitacaoItemRefDto[]>(props.values);

    const allValid = useMemo(() => {
        return values.every((x) => isUrlValido(x.url))
    }, [values])

    const hasDiff = useMemo(() => {
        return !lodash.isEqual(values, props.values);
    }, [values, props.values])

    return (<Grid>
        <Grid container justifyContent={'space-between'}>
            <Button disabled={!allValid || !hasDiff} color={'success'} onClick={() => {
                props.setValues(values);
            }}>Salvar</Button>
            <Button onClick={() => {
                setValues([...values, {
                    descricao: '',
                    url: ''
                }])
            }}>Adicionar</Button>
        </Grid>
        <Grid>
            {values.map((link) => (
                <RefMemo value={link} setValue={(v) => {
                    const newValues = [...values];
                    newValues[newValues.indexOf(link)] = v;
                    setValues(newValues);
                }}
                         onDelete={() => {
                             const newValues = [...values];
                             newValues.splice(newValues.indexOf(link), 1);
                             setValues(newValues);
                         }}
                >

                </RefMemo>
            ))}
        </Grid>
    </Grid>)
})

const ProdMemo = memo((props: {
    item: ICreateSolicitacaoItemDto,
    setItem: (v: ICreateSolicitacaoItemDto) => void,
    handleDelete: () => void
}) => {
    const {item, handleDelete, setItem} = props;
    const {addPopUp, closePopUp} = useAlert();


    return (<Grid key={item.produtoId} size={12} mb={1}>
        <Grid container>
            <Grid size={12}>
                <Grid container>
                    <Grid size={8}>
                        <TextField variant={'filled'}
                                   fullWidth
                                   value={item.nome} size={'small'}
                                   label={'Produto'}
                                   slotProps={{
                                       input: {
                                           endAdornment: <Badge badgeContent={item.links?.length ?? 0}
                                                                color={'primary'}>
                                               <IconButton onClick={() => {
                                                   addPopUp({
                                                       title: 'Links referentes',
                                                       content: <RefsPageMemo values={item.links ?? []}
                                                                              setValues={(v) => {
                                                                                  setItem({
                                                                                      ...item,
                                                                                      links: v
                                                                                  })
                                                                                  closePopUp()
                                                                              }}/>
                                                   })
                                               }}>
                                                   <LinkOutlined/>
                                               </IconButton>
                                           </Badge>
                                       }
                                   }}
                                   disabled/>
                    </Grid>
                    <Grid size={4}>
                        <Button fullWidth
                                endIcon={<DeleteOutlined/>}
                                sx={{height: '100%'}}
                                color={'error'}
                                variant={'contained'}
                                onClick={handleDelete}
                        >
                            Remover
                        </Button>
                    </Grid>
                </Grid>
            </Grid>
            <Grid size={12}>
                <Grid container>
                    <Grid size={6}>
                        <IntInputEntry value={item.quantidade}
                                       label={'Quantidade'}
                                       onChange={(e) => {
                                           setItem({...item, quantidade: e});
                                       }}
                        />
                    </Grid>
                    <Grid size={6}>
                        <MoneyInputEntry label={'Valor estimado'}
                                         textFieldProps={{
                                             variant: 'filled', slotProps: {
                                                 input: {
                                                     endAdornment:
                                                         <Typography
                                                             variant={'caption'}>{ToMoney((item.valorEstimado ?? 0)
                                                             * item.quantidade)}</Typography>,
                                                 },
                                             },
                                         }}
                                         onChange={(e) => {
                                             setItem({...item, valorEstimado: e})
                                         }}
                                         value={item.valorEstimado}/>
                    </Grid>
                </Grid>
            </Grid>

        </Grid>
    </Grid>)
}, (prev, next) => {
    return prev.item === next.item;
});

export function CreateSolicitacaoPage(props: CreateSolicitacaoPageProps) {
    const {createAlertFromResponse, createAlert} = useAlert();
    const {createSolicitacao} = useSolicitacao();
    const [loading, setLoading] = useState(false);

    const [solicitacao, setSolicitacao] = useState<ICreateSolicitacaoDto>({
        titulo: "",
        contaContabilId: 0,
        contratoId: 0,
        tipoSolicitacaoId: 0,
        recursoId: props.recurso?.id ?? 0,
        itens: [],
    });

    const [tipoSolicitacao, setTipoSolicitacao] = useState<ISolicitacaoTipo | undefined>();

    const parametros = useMemo<ISolicitacaoParametro[]>(() => {
        return tipoSolicitacao?.parametros ?? []
    }, [tipoSolicitacao])


    const handleSetContaContabil = useCallback((x?: IContaContabil) => {
        if (!x) return;
        setSolicitacao(prev => {
            if (x.id === prev.contaContabilId) return prev;
            return {...prev, contaContabilId: x.id};
        });
    }, []);

    const handleSetContrato = useCallback((x?: IContrato) => {
        setSolicitacao(prev => {
            const newId = x?.id ?? 0;
            if (newId === prev.contratoId) return prev;
            return {...prev, contratoId: newId};
        });
    }, []);

    const handleSetRecurso = useCallback((x?: IRecurso) => {
        if (!x) return;
        setSolicitacao(prev => {
            if (x.id === prev.recursoId) return prev;
            return {...prev, recursoId: x.id};
        });
    }, []);

    const handleSetTipoSolicitacao = useCallback((x?: ISolicitacaoTipo) => {
        if (!x) return;
        setSolicitacao(prev => {
            if (x.id === prev.tipoSolicitacaoId) return prev;
            return {...prev, tipoSolicitacao: x, tipoSolicitacaoId: x.id};
        });
        setTipoSolicitacao(x);
    }, []);

    const usaContrato = useMemo<IParamRule>(() => {
        const _param = parametros?.filter(x => x.valor == 'true' && (x.nomeTipo == 'UsaContrato' || x.nomeTipo == 'ObrigaContrato')) ?? [];

        return _param.some(x => x.nomeTipo == 'ObrigaContrato') ? 'obrigatorio' : _param.some(x => x.nomeTipo == 'UsaContrato')
    }, [parametros]);

    const usaContaContabil = useMemo<IParamRule>(() => {
        const _param = parametros?.filter(x => x.valor == 'true' && (x.nomeTipo == 'UsaContaContabil' || x.nomeTipo == 'ObrigaContaContabil')) ?? [];

        return _param.some(x => x.nomeTipo == 'UsaContaContabil') ? 'obrigatorio' : _param.some(x => x.nomeTipo == 'ObrigaContaContabil')
    }, [parametros]);

    const usaProdutos = useMemo<IParamRule>(() => {
        const _param = parametros?.filter(x => x.valor == 'true' && (x.nomeTipo == 'UsaProdutos' || x.nomeTipo == 'ObrigaProdutos')) ?? [];

        return _param.some(x => x.nomeTipo == 'UsaProdutos') ? 'obrigatorio' : _param.some(x => x.nomeTipo == 'ObrigaProdutos')
    }, [parametros]);

    const usaRecurso = useMemo<IParamRule>(() => {
        const _param = parametros?.filter(x => x.valor == 'true' && (x.nomeTipo == 'ObrigaRecurso' || x.nomeTipo == 'UsaRecurso')) ?? [];

        return _param.some(x => x.nomeTipo == 'ObrigaRecurso') ? 'obrigatorio' : _param.some(x => x.nomeTipo == 'UsaRecurso')
    }, [parametros]);

    const tituloValid = useMemo(() => {
        return solicitacao.titulo.length >= 5;
    }, [solicitacao.titulo])

    return (
        <DefaultContainer titulo={props.hideHeader ? '' : 'Abrindo nova solicitação'} component={Paper}>
            <Grid container justifyContent={'space-between'} mb={1}>
                <Grid/>
                <Grid>
                    <Button endIcon={<Upload/>}
                            color={'success'}
                            loading={loading}
                            variant={'outlined'}
                            onClick={() => {
                                createSolicitacao(solicitacao).then(x => {
                                    createAlertFromResponse(x);
                                    if (x.status == 'Success') {
                                        if (props.onCreate) {
                                            props.onCreate(x.items);
                                        }
                                    }
                                }).finally(() => setLoading(false));
                            }}>Enviar</Button>
                </Grid>
            </Grid>
            <Grid size={12}>
                <TextField fullWidth
                           error={!tituloValid}
                           value={solicitacao.titulo}
                           label={'Titulo'}
                           required
                           onChange={(e) => {
                               setSolicitacao(prev => ({...prev, titulo: e.target.value}));
                           }}/>
            </Grid>
            <Grid size={12}>
                <SolicitacaoTipoPesquisa required={true} onItemSelected={handleSetTipoSolicitacao}/>
            </Grid>
            {usaRecurso && <Grid size={12}>
                <RecursoPesquisa required={usaRecurso == 'obrigatorio'}
                                 onItemSelected={handleSetRecurso}/>
            </Grid>}
            {usaContrato && <Grid size={12}>
                <ContratoPesquisa onSelect={handleSetContrato}
                                  overrides={{required: usaContrato == 'obrigatorio'}}/>
            </Grid>}
            {usaContaContabil &&
                <Grid size={12}>
                    <ContaContabilPesquisa onItemSelected={handleSetContaContabil} textFieldProps={{
                        required: usaContaContabil == 'obrigatorio',
                    }}/>
                </Grid>}

            {usaProdutos && <Grid size={12} mt={1.5}>
                <Grid container justifyContent={'space-between'}>
                    <Grid/>
                    <Grid size={{xs: 12, sm: 10, md: 8}} mb={1.5} container>
                        <Grid size={10}>
                            <ProdutoPesquisa options={{clearOnSelect: true}}
                                             onItemSelected={(x) => {
                                                 if (!x) return;
                                                 setSolicitacao(prev => {
                                                     const itens = prev.itens ?? [];
                                                     if (itens.some(y => y.produtoId === x.id)) {
                                                         createAlert('Produto já está presente na solicitação', 'error', 3000);
                                                         return prev;
                                                     }
                                                     return {
                                                         ...prev,
                                                         itens: [...itens, {
                                                             quantidade: 1,
                                                             produtoId: x.id,
                                                             valorEstimado: x.ultimoPreco ?? 0,
                                                             nome: x.nome
                                                         }],
                                                     };
                                                 });
                                             }}
                            />
                        </Grid>
                        <Grid size={2}>
                            <PermissionableIcon permissao={['CriarFornecedor']} sx={{...fullHeight, ...fullWidth}}>
                                <AddOutlined/>
                            </PermissionableIcon>
                        </Grid>
                    </Grid>
                </Grid>
                {solicitacao.itens && solicitacao.itens.map((item, index) => {
                    return (<ProdMemo key={index} item={item} setItem={(it) => {
                            setSolicitacao(prev => {
                                const newItens = [...prev.itens ?? []];
                                newItens[index] = it;
                                return {...prev, itens: newItens};
                            })
                        }} handleDelete={() => {
                            setSolicitacao(prev => {
                                const newItens = [...prev.itens.filter(x => x.produtoId != item.produtoId)]
                                return {...prev, itens: newItens};
                            })
                        }}/>
                    );
                })}
            </Grid>}
        </DefaultContainer>);
}